import React from 'react';
import EmailBindSection from '../auth/EmailBindSection';
import ProfileMetaPanel from './ProfileMetaPanel';

const COPY = {
  en: {
    title: 'Account & security',
    subtitle: 'Email, password, and account metadata',
    email: 'Email',
    password: 'Password',
    passwordSet: 'Password set',
    passwordHint: 'Change your sign-in password',
    changePassword: 'Change password',
    accountDetails: 'Account details',
    noEmail: 'Not bound',
    verified: 'Verified',
    unverified: 'Not verified',
  },
  zh: {
    title: '账户与安全',
    subtitle: '邮箱、密码与账户信息',
    email: '邮箱',
    password: '密码',
    passwordSet: '已设置',
    passwordHint: '修改登录密码',
    changePassword: '修改密码',
    accountDetails: '账户详情',
    noEmail: '未绑定',
    verified: '已验证',
    unverified: '未验证',
  },
};

function EmailIcon() {
  return (
    <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true" fill="none">
      <rect x="2" y="5" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 7l8 5 8-5" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true" fill="none">
      <rect x="4" y="9" width="12" height="8" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M7 9V7a3 3 0 0 1 6 0v2"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true" fill="none">
      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10 9v5M10 6.5h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SettingsItemIcon({ children, tone = 'blue' }) {
  return (
    <span className={`profile-v2-settings-item-icon is-${tone}`} aria-hidden="true">
      {children}
    </span>
  );
}

function SettingsSectionHeader({ icon, tone, label, value, badge, badgeClass, action }) {
  return (
    <div className="profile-v2-settings-section-head">
      <SettingsItemIcon tone={tone}>{icon}</SettingsItemIcon>
      <div className="profile-v2-settings-item-main">
        <span className="profile-v2-settings-item-label">{label}</span>
        {value ? <span className="profile-v2-settings-item-value">{value}</span> : null}
      </div>
      <div className="profile-v2-settings-item-trail">
        {badge ? (
          <span className={`profile-v2-inline-badge ${badgeClass || ''}`}>{badge}</span>
        ) : null}
        {action || null}
      </div>
    </div>
  );
}

function ProfileSettingsList({
  lang,
  email,
  emailVerified,
  profile,
  onChangePassword,
  onEmailVerified,
  onCopyUuid,
  animationDelay = 0,
}) {
  const t = COPY[lang === 'zh' ? 'zh' : 'en'];

  const emailValue = email || t.noEmail;
  const emailBadge = email ? (emailVerified ? t.verified : t.unverified) : null;
  const emailBadgeClass = emailVerified ? 'is-verified' : 'is-unverified';

  return (
    <section
      className="profile-v2-panel profile-v2-settings profile-v2-enter"
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      <header className="profile-v2-panel-head">
        <span className="profile-v2-panel-badge is-violet" aria-hidden="true">
          <LockIcon />
        </span>
        <div className="profile-v2-panel-head-text">
          <h2 className="profile-v2-panel-title">{t.title}</h2>
          <p className="profile-v2-panel-subtitle">{t.subtitle}</p>
        </div>
      </header>

      <div className="profile-v2-settings-group-box">
        <section className="profile-v2-settings-section">
          <SettingsSectionHeader
            icon={<EmailIcon />}
            tone="blue"
            label={t.email}
            value={emailValue}
            badge={emailBadge}
            badgeClass={emailBadgeClass}
          />
          <div className="profile-v2-settings-section-body">
            <EmailBindSection
              email={email}
              emailVerified={emailVerified}
              lang={lang}
              embedded
              onVerified={onEmailVerified}
            />
          </div>
        </section>

        <section className="profile-v2-settings-section">
          <SettingsSectionHeader
            icon={<LockIcon />}
            tone="indigo"
            label={t.password}
            value={t.passwordHint}
            badge={t.passwordSet}
            badgeClass="is-neutral"
            action={
              <button
                type="button"
                className="profile-v2-settings-action-btn"
                onClick={() => typeof onChangePassword === 'function' && onChangePassword()}
              >
                {t.changePassword}
              </button>
            }
          />
        </section>

        <section className="profile-v2-settings-section">
          <SettingsSectionHeader
            icon={<InfoIcon />}
            tone="slate"
            label={t.accountDetails}
          />
          <div className="profile-v2-settings-section-body">
            <ProfileMetaPanel lang={lang} profile={profile} onCopyUuid={onCopyUuid} />
          </div>
        </section>
      </div>
    </section>
  );
}

export default ProfileSettingsList;
